import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
import { useContainerSize, useDrawingElementsProps } from '../hooks';
import useDrawingElements from '../hooks/useDrawingElements';
import type { ChartInnerProps, PolarHandlers, SecondAxisDefinition } from '../internalTypes';
import type { ChartCardChartDefinition, ChartDataRow } from '../types';
import { getAxisDescription, getChartMarginsAndAxisSpaces, getFirstOrThirdAxisDetails, getSecondAxisDetails } from '../utils/axis';
import ChartDevtools from './ChartDevTools';
import ChartElement, { type ChartElementProps } from './ChartElement';
import ChartElementContextProvider from './ChartElementContextProvider';
import ChartTooltip from './ChartTooltip';
import ChartZoomOverlay, { type ChartZoomOverlayOnZoomHandler } from './ChartZoomOverlay';
import FirstAxis from './FirstAxix';
import SecondAxis from './SecondAxis';
import StatusLegend from './StatusLegend';
const getChartElementPropsFromAxis = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(chart: ChartCardChartDefinition<DataKey, DataRow>): ChartElementProps => {
  const {
    type: chartType,
    otherType: otherChartType,
    layout = 'horizontal'
  } = chart;
  if ((chartType === 'bar' || chartType === 'line') && (otherChartType === 'bar' || otherChartType === 'line')) {
    return {
      chartType,
      otherChartType,
      layout
    };
  }
  if (chartType === 'bar' || chartType === 'line' || chartType === 'gradientLine' || chartType === 'area') {
    return {
      chartType,
      otherChartType: undefined,
      layout
    };
  }
  if (chartType === 'pie' || chartType === 'donut') {
    return {
      chartType,
      otherChartType: undefined,
      layout: 'centric'
    };
  }
  chartType satisfies 'scatter';
  return {
    chartType: 'scatter',
    otherChartType: undefined,
    layout
  };
};
const Chart = t0 => {
  const $ = _c(142);
  const {
    size,
    width,
    height,
    tooltipContent: tooltipContentProp,
    chart,
    respectColors,
    axisDrawing,
    chartDrawing,
    drawingColorMaps,
    data,
    dimensionsMap,
    metricsMap,
    choicesMap,
    elementsDescriptionsMap,
    selections,
    transposedMetricsLists,
    chartMarginRef,
    containerRef,
    allowZooming,
    zoomedIn,
    firstAxisDomain,
    secondAxisDomains,
    dispatch,
    t
  } = t0;
  const defaults = useChartCardDefaults();
  const {
    view: viewType,
    type: chartType,
    otherType: otherChartType,
    layout: t1,
    firstAxis,
    secondAxis,
    otherSecondAxis,
    thirdAxis,
    customProps,
    otherCustomProps
  } = chart;
  const layout = t1 === undefined ? "horizontal" : t1;
  const {
    width: containerWidth
  } = useContainerSize(containerRef);
  const [zooming, setZooming] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  let t2;
  if ($[0] !== choicesMap || $[1] !== dimensionsMap || $[2] !== elementsDescriptionsMap || $[3] !== firstAxis || $[4] !== metricsMap || $[5] !== selections) {
    t2 = getFirstOrThirdAxisDetails(firstAxis, {
      choicesMap,
      dimensionsMap,
      metricsMap,
      elementsDescriptionsMap,
      selections
    });
    $[0] = choicesMap;
    $[1] = dimensionsMap;
    $[2] = elementsDescriptionsMap;
    $[3] = firstAxis;
    $[4] = metricsMap;
    $[5] = selections;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const firstAxisDetails = t2;
  let t3;
  if ($[7] !== choicesMap || $[8] !== dimensionsMap || $[9] !== elementsDescriptionsMap || $[10] !== metricsMap || $[11] !== selections || $[12] !== thirdAxis) {
    t3 = getFirstOrThirdAxisDetails(thirdAxis, {
      choicesMap,
      dimensionsMap,
      metricsMap,
      elementsDescriptionsMap,
      selections
    });
    $[7] = choicesMap;
    $[8] = dimensionsMap;
    $[9] = elementsDescriptionsMap;
    $[10] = metricsMap;
    $[11] = selections;
    $[12] = thirdAxis;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const thirdAxisDetails = t3;
  const {
    dataKey: firstAxisDataKey,
    label: firstAxisLabel,
    isDate: firstAxisIsDate
  } = firstAxisDetails;
  const firstAxisAngledLabels = axisDrawing?.firstAxis?.hide !== true && chartType !== "scatter" && layout === "horizontal" && !firstAxisIsDate && (chartType === "bar" || otherChartType === "bar");
  let t4;
  if ($[14] !== firstAxisDataKey) {
    t4 = (data_0, index) => {
      setHoveredSlice(data_0[firstAxisDataKey] as string);
      setHoveredIndex(index);
    };
    $[14] = firstAxisDataKey;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handlePieMouseEnter = t4;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setHoveredSlice(null);
      setHoveredIndex(null);
    };
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handlePieMouseLeave = t5;
  const t6 = axisDrawing?.secondAxis;
  let t7;
  if ($[17] !== axisDrawing?.otherSecondAxis || $[18] !== chartType || $[19] !== choicesMap || $[20] !== customProps || $[21] !== dimensionsMap || $[22] !== metricsMap || $[23] !== otherChartType || $[24] !== otherCustomProps || $[25] !== otherSecondAxis || $[26] !== secondAxis || $[27] !== selections || $[28] !== t6 || $[29] !== transposedMetricsLists) {
    const secondAxisDefinitions = [{
      chartType,
      axisType: "second",
      axis: secondAxis,
      customProps,
      axisDrawing: t6
    }];
    if (otherSecondAxis != null) {
      if (otherChartType == null) {
        secondAxisDefinitions.push({
          chartType,
          axisType: "otherSecond",
          axis: otherSecondAxis,
          customProps: otherCustomProps,
          axisDrawing: axisDrawing?.otherSecondAxis
        });
      } else {
        secondAxisDefinitions.push({
          chartType: otherChartType,
          axisType: "otherSecond",
          axis: otherSecondAxis,
          customProps: otherCustomProps,
          axisDrawing: axisDrawing?.otherSecondAxis
        });
      }
    }
    t7 = getSecondAxisDetails({
      definitions: secondAxisDefinitions,
      choicesMap,
      dimensionsMap,
      metricsMap,
      transposedMetricsLists,
      selections
    });
    $[17] = axisDrawing?.otherSecondAxis;
    $[18] = chartType;
    $[19] = choicesMap;
    $[20] = customProps;
    $[21] = dimensionsMap;
    $[22] = metricsMap;
    $[23] = otherChartType;
    $[24] = otherCustomProps;
    $[25] = otherSecondAxis;
    $[26] = secondAxis;
    $[27] = selections;
    $[28] = t6;
    $[29] = transposedMetricsLists;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const secondAxisDetails = t7;
  let firstAxisDescription;
  let t8;
  if ($[31] !== axisDrawing || $[32] !== chartType || $[33] !== containerWidth || $[34] !== data || $[35] !== defaults || $[36] !== elementsDescriptionsMap || $[37] !== firstAxis || $[38] !== firstAxisAngledLabels || $[39] !== firstAxisDataKey || $[40] !== layout || $[41] !== viewType) {
    firstAxisDescription = getAxisDescription(firstAxis, elementsDescriptionsMap, firstAxisDataKey);
    t8 = getChartMarginsAndAxisSpaces({
      defaults,
      viewType,
      chartType,
      axisDrawing,
      firstAxisAngledLabels,
      containerWidth,
      layout,
      firstAxisDescription,
      firstAxisDataKey,
      data
    });
    $[31] = axisDrawing;
    $[32] = chartType;
    $[33] = containerWidth;
    $[34] = data;
    $[35] = defaults;
    $[36] = elementsDescriptionsMap;
    $[37] = firstAxis;
    $[38] = firstAxisAngledLabels;
    $[39] = firstAxisDataKey;
    $[40] = layout;
    $[41] = viewType;
    $[42] = firstAxisDescription;
    $[43] = t8;
  } else {
    firstAxisDescription = $[42];
    t8 = $[43];
  }
  const chartMarginsAndAxisSpaces = t8;
  let t10;
  let t9;
  if ($[44] !== chartMarginRef || $[45] !== chartMarginsAndAxisSpaces) {
    t9 = () => {
      chartMarginRef.current = chartMarginsAndAxisSpaces;
    };
    t10 = [chartMarginsAndAxisSpaces, chartMarginRef];
    $[44] = chartMarginRef;
    $[45] = chartMarginsAndAxisSpaces;
    $[46] = t10;
    $[47] = t9;
  } else {
    t10 = $[46];
    t9 = $[47];
  }
  useEffect(t9, t10);
  const {
    margin: chartMargin,
    firstAxisSpace,
    visibleLabels
  } = chartMarginsAndAxisSpaces;
  const t11 = thirdAxisDetails ?? firstAxisDetails;
  let t12;
  if ($[48] !== chart || $[49] !== chartDrawing || $[50] !== drawingColorMaps || $[51] !== elementsDescriptionsMap || $[52] !== respectColors || $[53] !== secondAxisDetails || $[54] !== t11) {
    t12 = {
      chart,
      dimensionAxisDetails: t11,
      elementsDescriptionsMap,
      secondAxisDetails,
      respectColors,
      chartDrawing,
      drawingColorMaps
    };
    $[48] = chart;
    $[49] = chartDrawing;
    $[50] = drawingColorMaps;
    $[51] = elementsDescriptionsMap;
    $[52] = respectColors;
    $[53] = secondAxisDetails;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  const drawingElementsProps = useDrawingElementsProps(t12);
  let t13;
  bb0: {
    if (viewType !== "polar") {
      t13 = undefined;
      break bb0;
    }
    let t14;
    if ($[56] !== handlePieMouseEnter || $[57] !== hoveredIndex) {
      t14 = {
        onMouseEnter: handlePieMouseEnter,
        onMouseLeave: handlePieMouseLeave,
        hoveredIndex
      };
      $[56] = handlePieMouseEnter;
      $[57] = hoveredIndex;
      $[58] = t14;
    } else {
      t14 = $[58];
    }
    t13 = t14;
  }
  const polarHandlers = t13;
  let t14;
  if ($[59] !== data || $[60] !== drawingColorMaps || $[61] !== drawingElementsProps || $[62] !== polarHandlers) {
    t14 = {
      drawingElementsProps,
      drawingColorMaps,
      data,
      polarHandlers
    };
    $[59] = data;
    $[60] = drawingColorMaps;
    $[61] = drawingElementsProps;
    $[62] = polarHandlers;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  const drawingElements = useDrawingElements(t14);
  let t15;
  if ($[64] !== chartType || $[65] !== drawingColorMaps || $[66] !== elementsDescriptionsMap || $[67] !== firstAxisDetails || $[68] !== secondAxisDetails || $[69] !== selections || $[70] !== t || $[71] !== thirdAxisDetails || $[72] !== tooltipContentProp) {
    t15 = <ChartTooltip chartType={chartType} tooltipContentProp={tooltipContentProp} firstAxisDetails={firstAxisDetails} secondAxisDetails={secondAxisDetails} thirdAxisDetails={thirdAxisDetails} elementsDescriptionsMap={elementsDescriptionsMap} drawingColorMaps={drawingColorMaps} selections={selections} t={t} />;
    $[64] = chartType;
    $[65] = drawingColorMaps;
    $[66] = elementsDescriptionsMap;
    $[67] = firstAxisDetails;
    $[68] = secondAxisDetails;
    $[69] = selections;
    $[70] = t;
    $[71] = thirdAxisDetails;
    $[72] = tooltipContentProp;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  const tooltipElement = t15;
  let t16;
  if ($[74] !== dispatch || $[75] !== firstAxisDataKey || $[76] !== firstAxisDomain || $[77] !== secondAxisDomains) {
    t16 = t17 => {
      const {
        firstDomain,
        secondDomain,
        otherSecondDomain
      } = t17;
      if (firstDomain == null && secondDomain == null && otherSecondDomain == null) {
        dispatch({
          type: "resetZooming"
        });
        return;
      }
      if (otherSecondDomain != null) {
        dispatch({
          type: "zoomIn",
          firstAxisDomainDataKey: firstAxisDataKey,
          firstAxisDomain: firstDomain ?? firstAxisDomain,
          secondAxisDomains: [secondDomain ?? secondAxisDomains[0], otherSecondDomain ?? secondAxisDomains[1]]
        });
        return;
      }
      dispatch({
        type: "zoomIn",
        firstAxisDomainDataKey: firstAxisDataKey,
        firstAxisDomain: firstDomain ?? firstAxisDomain,
        secondAxisDomains: [secondDomain ?? secondAxisDomains[0]]
      });
    };
    $[74] = dispatch;
    $[75] = firstAxisDataKey;
    $[76] = firstAxisDomain;
    $[77] = secondAxisDomains;
    $[78] = t16;
  } else {
    t16 = $[78];
  }
  const onZoomHandler = t16;
  let t17;
  if ($[79] !== chart) {
    t17 = getChartElementPropsFromAxis(chart);
    $[79] = chart;
    $[80] = t17;
  } else {
    t17 = $[80];
  }
  const chartElementProps = t17;
  const t18 = zooming ? "true" : undefined;
  let t19;
  if ($[81] !== chart || $[82] !== elementsDescriptionsMap || $[83] !== firstAxisDataKey || $[84] !== hoveredSlice || $[85] !== secondAxisDetails || $[86] !== t || $[87] !== viewType || $[88] !== width) {
    t19 = <StatusLegend viewType={viewType} chartWidth={width} chart={chart} firstAxisDataKey={firstAxisDataKey} elementsDescriptionsMap={elementsDescriptionsMap} secondAxisDetails={secondAxisDetails} hoveredSlice={hoveredSlice} t={t} />;
    $[81] = chart;
    $[82] = elementsDescriptionsMap;
    $[83] = firstAxisDataKey;
    $[84] = hoveredSlice;
    $[85] = secondAxisDetails;
    $[86] = t;
    $[87] = viewType;
    $[88] = width;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  const t20 = axisDrawing?.firstAxis;
  let t21;
  if ($[90] !== chartType || $[91] !== firstAxis || $[92] !== firstAxisAngledLabels || $[93] !== firstAxisDataKey || $[94] !== firstAxisDescription || $[95] !== firstAxisDomain || $[96] !== firstAxisLabel || $[97] !== firstAxisSpace || $[98] !== layout || $[99] !== t || $[100] !== t20 || $[101] !== visibleLabels) {
    t21 = <FirstAxis id={firstAxis} chartType={chartType} layout={layout} axisDescription={firstAxisDescription} firstAxisDomain={firstAxisDomain} firstAxisAngledLabels={firstAxisAngledLabels} axisDrawing={t20} dataKey={firstAxisDataKey} label={firstAxisLabel} axisSpace={firstAxisSpace} visibleLabels={visibleLabels} t={t} />;
    $[90] = chartType;
    $[91] = firstAxis;
    $[92] = firstAxisAngledLabels;
    $[93] = firstAxisDataKey;
    $[94] = firstAxisDescription;
    $[95] = firstAxisDomain;
    $[96] = firstAxisLabel;
    $[97] = firstAxisSpace;
    $[98] = layout;
    $[99] = t;
    $[100] = t20;
    $[101] = visibleLabels;
    $[102] = t21;
  } else {
    t21 = $[102];
  }
  let t22;
  if ($[103] !== elementsDescriptionsMap || $[104] !== layout || $[105] !== secondAxisDetails || $[106] !== secondAxisDomains || $[107] !== size) {
    let t23;
    if ($[109] !== elementsDescriptionsMap || $[110] !== layout || $[111] !== secondAxisDomains || $[112] !== size) {
      t23 = (secondAxisDetail, index_0) => <SecondAxis key={secondAxisDetail.axis} layout={layout} size={size} axisIndex={index_0} secondAxisDetails={secondAxisDetail} secondAxisDomains={secondAxisDomains} elementsDescriptionsMap={elementsDescriptionsMap} />;
      $[109] = elementsDescriptionsMap;
      $[110] = layout;
      $[111] = secondAxisDomains;
      $[112] = size;
      $[113] = t23;
    } else {
      t23 = $[113];
    }
    t22 = secondAxisDetails.map(t23);
    $[103] = elementsDescriptionsMap;
    $[104] = layout;
    $[105] = secondAxisDetails;
    $[106] = secondAxisDomains;
    $[107] = size;
    $[108] = t22;
  } else {
    t22 = $[108];
  }
  const t23 = !allowZooming && tooltipElement;
  const t24 = !allowZooming && drawingElements;
  let t25;
  if ($[114] !== allowZooming || $[115] !== drawingElements || $[116] !== onZoomHandler || $[117] !== tooltipElement) {
    t25 = allowZooming && <ChartZoomOverlay onZoom={onZoomHandler} setZooming={setZooming} tooltipElement={tooltipElement} drawingElements={drawingElements} />;
    $[114] = allowZooming;
    $[115] = drawingElements;
    $[116] = onZoomHandler;
    $[117] = tooltipElement;
    $[118] = t25;
  } else {
    t25 = $[118];
  }
  let t26;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ChartDevtools />;
    $[119] = t26;
  } else {
    t26 = $[119];
  }
  let t27;
  if ($[120] !== chartType || $[121] !== firstAxis || $[122] !== height || $[123] !== layout || $[124] !== otherSecondAxis || $[125] !== secondAxis || $[126] !== size || $[127] !== t19 || $[128] !== t21 || $[129] !== t22 || $[130] !== t23 || $[131] !== t24 || $[132] !== t25 || $[133] !== width || $[134] !== zoomedIn) {
    t27 = <ChartElementContextProvider layout={layout} chartType={chartType} size={size} width={width} height={height} zoomedIn={zoomedIn} firstAxisId={firstAxis} secondAxisId={secondAxis} otherSecondAxisId={otherSecondAxis}>{t19}{t21}{t22}{t23}{t24}{t25}{t26}</ChartElementContextProvider>;
    $[120] = chartType;
    $[121] = firstAxis;
    $[122] = height;
    $[123] = layout;
    $[124] = otherSecondAxis;
    $[125] = secondAxis;
    $[126] = size;
    $[127] = t19;
    $[128] = t21;
    $[129] = t22;
    $[130] = t23;
    $[131] = t24;
    $[132] = t25;
    $[133] = width;
    $[134] = zoomedIn;
    $[135] = t27;
  } else {
    t27 = $[135];
  }
  let t28;
  if ($[136] !== chartElementProps || $[137] !== chartMargin || $[138] !== data || $[139] !== t18 || $[140] !== t27) {
    t28 = <ChartElement {...chartElementProps} data={data} data-zooming={t18} margin={chartMargin}>{t27}</ChartElement>;
    $[136] = chartElementProps;
    $[137] = chartMargin;
    $[138] = data;
    $[139] = t18;
    $[140] = t27;
    $[141] = t28;
  } else {
    t28 = $[141];
  }
  return t28;
};
export default Chart;